/**
 * compress-image.ts
 *
 * Compresses a logo image File to a base64 JPEG data URL.
 *
 * Rules (per PRD §1.11a):
 *  - Maximum dimensions: 300 × 80 px (aspect-ratio preserved, never upscaled)
 *  - JPEG quality: 85 %
 *  - Hard limit: rejects files larger than 10 MB before any processing
 */

const MAX_WIDTH = 300;
const MAX_HEIGHT = 80;
const JPEG_QUALITY = 0.85;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

/** Image formats @react-pdf/renderer is able to decode. */
export type PdfSafeImageFormat = 'png' | 'jpeg' | 'svg';

/**
 * Identifies the image format from its magic bytes, falling back to the
 * declared MIME type for SVG (which has no reliable binary signature).
 *
 * Returns null for anything @react-pdf/renderer cannot decode (WebP, GIF,
 * AVIF, HEIC…) and for bytes whose signature contradicts the declared type.
 */
export function detectPdfSafeImageFormat(
  bytes: Uint8Array,
  mimeType?: string,
): PdfSafeImageFormat | null {
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 &&
    bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a
  ) {
    return 'png';
  }
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'jpeg';
  }
  if (mimeType?.split(';')[0].trim().toLowerCase() === 'image/svg+xml') {
    return 'svg';
  }
  return null;
}

/**
 * Rasterises any browser-decodable image (WebP, GIF, AVIF…) to a base64 PNG
 * data URL, scaled down to fit within the given bounds.
 */
export async function convertImageToPngDataUrl(
  source: Blob,
  maxWidth = 600,
  maxHeight = 200,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(source);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { naturalWidth: w, naturalHeight: h } = img;
      if (!w || !h) {
        reject(new Error('Image has no intrinsic dimensions.'));
        return;
      }
      if (w > maxWidth || h > maxHeight) {
        const scale = Math.min(maxWidth / w, maxHeight / h);
        w = Math.max(1, Math.round(w * scale));
        h = Math.max(1, Math.round(h * scale));
      }

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas 2D context is unavailable in this browser.'));
        return;
      }

      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to decode the image.'));
    };

    img.src = objectUrl;
  });
}

/**
 * Compresses a logo image file and returns a base64-encoded JPEG data URL.
 *
 * @param file - The image File selected by the user (any browser-supported format).
 * @returns A Promise that resolves with the compressed base64 JPEG data URL.
 * @throws An error if the file exceeds 10 MB or if the canvas API is unavailable.
 */
export async function compressLogoImage(file: File): Promise<string> {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(
      `Logo image must be smaller than 10 MB. The selected file is ${(file.size / (1024 * 1024)).toFixed(1)} MB.`,
    );
  }

  return new Promise<string>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      // Calculate scaled dimensions — never upscale, always preserve aspect ratio
      let { naturalWidth: w, naturalHeight: h } = img;

      if (w > MAX_WIDTH || h > MAX_HEIGHT) {
        const scaleByWidth = MAX_WIDTH / w;
        const scaleByHeight = MAX_HEIGHT / h;
        const scale = Math.min(scaleByWidth, scaleByHeight);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas 2D context is unavailable in this browser.'));
        return;
      }

      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load the selected image file.'));
    };

    img.src = objectUrl;
  });
}
